import React, { useState, useRef, useEffect } from "react";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

function CvsUpload({ onNext, sessionId }) {
  const fileInputRef = useRef();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle, uploading, uploaded
  const [error, setError] = useState(""); // 錯誤訊息
  const [openDialog, setOpenDialog] = useState(false); // 控制 Dialog

  const handleBoxClick = () => {
    if (uploadStatus !== "uploading") fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadStatus("uploading");
      try {
        const formData = new FormData();
        formData.append("uploadfile", file); //使用者上傳的檔案
        formData.append("cvsstore", "default"); // 使用預設值

        const response = await fetch("https://v2.api.tnml-tools.lch.tw/circulation/interlibrary/cvs/import", {
          method: "POST",
          headers: {
            "X-Session-ID": sessionId
          },
          body: formData,
        });
        if (!response.ok) {
          throw new Error("檔案上傳失敗，請再試一次。");
        }
        setUploadStatus("uploaded");

      } catch (error) {
        setUploadStatus("idle");
        setError("檔案有誤，請重新上傳。");
        setOpenDialog(true);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } else {
      setSelectedFile(null);
      setUploadStatus("idle");
    }
  };

  // 狀態對應顏色
  let borderColor = "#90caf9";
  let iconColor = "#1976d2";
  let textColor = "#1976d2";
  if (uploadStatus === "uploaded") {
    borderColor = "#43a047";
    iconColor = "#43a047";
    textColor = "#388e3c";
  } else if (uploadStatus === "uploading") {
    borderColor = "#ffa726";
    iconColor = "#ffa726";
    textColor = "#f57c00";
  }

  return (
    <div style={{ height: "100%", position: "relative" }}>
      {/* 彈跳對話框 */}
      <Dialog open={openDialog} onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle>上傳失敗</DialogTitle>
        <DialogContent>
          {error}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} autoFocus>
            確定
          </Button>
        </DialogActions>
      </Dialog>
      <div
        onClick={handleBoxClick}
        style={{
          border: `2px ${uploadStatus === "uploaded" ? "solid" : "dashed"} ${borderColor}`,
          borderRadius: 12,
          padding: "40px 0",
          textAlign: "center",
          color: textColor,
          background: "#f4f8ff",
          cursor: uploadStatus === "uploading" ? "not-allowed" : "pointer",
          marginBottom: 32,
          transition: "border-color 0.2s, color 0.2s",
          opacity: uploadStatus === "uploading" ? 0.7 : 1,
        }}
      >
        <CloudUploadIcon
          style={{
            fontSize: 48,
            marginBottom: 8,
            color: iconColor,
          }}
        />
        <div style={{ fontSize: 18, fontWeight: "bold" }}>
          {uploadStatus === "idle" && !selectedFile && (
            <>
              請把流通系統的超商報表上傳到這裡
              <br />
              (流通=&gt;移轉(收到項目)=&gt;報表=&gt;所有項目)
            </>
          )}
          {uploadStatus === "uploading" && (
            <span style={{ color: "#f57c00" }}>伺服器處理中，請稍候...</span>
          )}
          {uploadStatus === "uploaded" && selectedFile && (
            <span>
              上傳成功：
              <span style={{ color: "#388e3c" }}>{selectedFile.name}</span>
            </span>
          )}
        </div>
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          disabled={uploadStatus === "uploading"}
        />
      </div>
      {/* 下一步按鈕：一直顯示，僅上傳成功時可點選 */}
      <Button
        variant="contained"
        color="primary"
        onClick={onNext}
        disabled={uploadStatus !== "uploaded"}
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          borderRadius: "24px",
          fontSize: 18,
          fontWeight: "bold",
          px: 4,
          py: 1.5,
          boxShadow: 2,
        }}
      >
        下一步
      </Button>
    </div>
  );
}

function CvsScan({ onNext, sessionId }) {
  const [barcode, setBarcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [apiMsg, setApiMsg] = useState("");
  const inputRef = useRef(); // 新增

  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
    setError("");
    setApiMsg("");
  };

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcode.trim()) return;
    setLoading(true);
    setError("");
    setApiMsg("");
    try {
      const formData = new FormData();
      formData.append("barcode", barcode);
      const response = await fetch("https://v2.api.tnml-tools.lch.tw/circulation/interlibrary/cvs/scan", {
        method: "POST",
        headers: {
          "X-Session-ID": sessionId,
        },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || result.error) {
        throw new Error(result.error || "條碼處理失敗，請重試");
      }
      setApiMsg(result.message || `${barcode} 條碼儲存成功`);
      setBarcode(""); // 清空，準備刷下一筆
      // 讓焦點回到輸入框
      setTimeout(() => {
        inputRef.current && inputRef.current.focus();
      }, 0);
    } catch (err) {
      setError(err.message || "條碼處理失敗");
      // 讓焦點回到輸入框
      setTimeout(() => {
        inputRef.current && inputRef.current.focus();
      }, 0);
    } finally {
      setLoading(false);
      setBarcode(""); // 清空，準備刷下一筆
      setTimeout(() => {
        inputRef.current && inputRef.current.focus();
      }, 0);
    }
  };

  useEffect(() => {
    if (!loading) {
      inputRef.current && inputRef.current.focus();
    }
  }, [loading]);

  return (
    <div style={{ height: "100%", position: "relative" }}>
      <form onSubmit={handleBarcodeSubmit}>
        <input
          ref={inputRef} // 綁定 ref
          type="text"
          value={barcode}
          onChange={handleBarcodeChange}
          disabled={loading}
          autoFocus
          placeholder="請刷讀要移送超商的書籍條碼號"
          style={{
            fontSize: 20,
            padding: "12px 16px",
            borderRadius: 8,
            border: "1px solid #90caf9",
            width: "100%",
            marginBottom: 16,
            boxSizing: "border-box",
          }}
        />
        {error && (
          <div style={{ color: "#d32f2f", marginBottom: 8 }}>{error}</div>
        )}
        {apiMsg && (
          <div style={{ color: "#388e3c", marginBottom: 8 }}>{apiMsg}</div>
        )}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={loading || !barcode.trim()}
          sx={{
            borderRadius: "24px",
            fontSize: 18,
            fontWeight: "bold",
            px: 4,
            py: 1.5,
            boxShadow: 2,
          }}
        >
          {loading ? "處理中..." : "送出條碼"}
        </Button>
      </form>
      <Button
        variant="contained"
        color="primary"
        onClick={onNext}
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          borderRadius: "24px",
          fontSize: 18,
          fontWeight: "bold",
          px: 4,
          py: 1.5,
          boxShadow: 2,
        }}
      >
        下一步
      </Button>
    </div>
  );
}

function CvsReport({ sessionId }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false); // 新增 loading 狀態

  const handleDownload = async (url) => {
    setLoading(true); // 開始載入
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Session-ID": sessionId,
        },
      });

      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("application/json")) {
        const result = await response.json();
        if (result.error) {
          throw new Error(result.error);
        }
        throw new Error("下載失敗，請稍後再試");
      } else if (!response.ok) {
        throw new Error("下載失敗，請稍後再試");
      }

      let filename = "報表.pdf";
      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        const match = disposition.match(/filename\s*=\s*"?([^"]+)"?/i);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1].trim());
        }
      }
      const blob = await response.blob();
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlObj);
    } catch (err) {
      setErrorMsg(err.message || "下載失敗");
      setOpenDialog(true);
    } finally {
      setLoading(false); // 結束載入
    }
  };

  return (
    <>
      {/* 載入中 Dialog */}
      <Dialog open={loading} onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle>請稍候</DialogTitle>
        <DialogContent style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <CircularProgress size={32} />
          <span style={{ fontSize: 18 }}>檔案產生中，請稍候...</span>
        </DialogContent>
      </Dialog>
      {/* 錯誤 Dialog */}
      <Dialog open={openDialog} onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle>下載失敗</DialogTitle>
        <DialogContent>
          <div style={{ color: "#d32f2f", fontSize: 18, fontWeight: "bold", minWidth: 200 }}>
            {errorMsg}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} autoFocus>
            確定
          </Button>
        </DialogActions>
      </Dialog>
      <div
        style={{
          display: "flex",
          flex: 1,
          gap: 32,
          alignItems: "stretch",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleDownload("https://v2.api.tnml-tools.lch.tw/circulation/interlibrary/cvs/export/signing")}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            borderRadius: "18px",
            fontSize: 22,
            fontWeight: "bold",
            px: 0,
            py: 0,
            boxShadow: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          下載超商簽名總表(A4列印)
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => handleDownload("https://v2.api.tnml-tools.lch.tw/circulation/interlibrary/cvs/export/transitslip")}
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            borderRadius: "18px",
            fontSize: 22,
            fontWeight: "bold",
            px: 0,
            py: 0,
            boxShadow: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          下載超商夾書單(A6列印)
        </Button>
      </div>
    </>
  );
}

function CVSTab({ sessionId }) {
  const [tab, setTab] = useState(0);
  const tabList = [
    { label: "1. 匯入檔案", component: <CvsUpload onNext={() => setTab(1)} sessionId={sessionId} /> },
    { label: "2. 刷讀條碼", component: <CvsScan onNext={() => setTab(2)} sessionId={sessionId} /> },
    { label: "3. 下載報表", component: <CvsReport sessionId={sessionId} /> },
  ];

  return (
    <div
      style={{
        maxWidth: 1080,
        width: "100%",
        minHeight: 420, // 固定最小高度
        height: 420, // 固定高度（可依需求調整）
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        padding: 24,
        margin: "0 auto",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* 分頁籤 */}
      <div
        style={{
          display: "flex",
          borderBottom: "2px solid #eee",
          marginBottom: 24,
        }}
      >
        {tabList.map((t, idx) => (
          <button
            key={t.label}
            /*onClick={() => setTab(idx)}*/ //不讓使用者點分頁
            style={{
              flex: 1,
              padding: "12px 0",
              background: tab === idx ? "#007bff" : "transparent",
              color: tab === idx ? "#fff" : "#007bff",
              border: "none",
              borderBottom:
                tab === idx
                  ? "2px solid #007bff"
                  : "2px solid transparent",
              fontWeight: tab === idx ? "bold" : "normal",
              cursor: "pointer",
              transition: "background 0.2s",
            }}
            disabled={tab < idx}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* 分頁內容 */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {tabList[tab].component}
      </div>
    </div>
  );
}

export default CVSTab;